import { useEffect, useMemo, useState, useRef } from "react";

import { apiRequest, ensureCsrfToken, getLegacyUrl } from "../../api";

import InvoiceCheckDetailModal from "../../components/InvoiceCheckDetailModal";

import ProcessorInvoiceEditModal from "../../components/ProcessorInvoiceEditModal";

import ProcessorViewShell from "./ProcessorViewShell";

import { openInvoiceData } from "../../utils/invoiceDisplay";
import { openInvoicePdf } from "../../utils/openPdf";

function formatAmount(value) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  }).format(numericValue);
}



function formatDate(value) {
  if (!value) {
    return "-";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleDateString();
}

function getPendingDuration(createdAt) {
  if (!createdAt) {
    return "-";
  }

  const createdTime = new Date(createdAt);
  const now = new Date();
  let diffMs = now - createdTime;
  if (diffMs < 0) {
    diffMs = 0;
  }

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (days > 0) {
    return `${days}d ${hours % 24}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  }
  return `${minutes}m`;
}





function TrailModal({ trail, loading, onClose }) {
  useEffect(() => {
    if (!trail && !loading) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [trail, loading, onClose]);

  if (!trail && !loading) {
    return null;
  }

  return (
    <div className="processor-modal" role="presentation" onClick={onClose}>
      <div
        className="processor-modal__card processor-modal__card--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="processor-trail-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="processor-modal__header">
          <div>
            <p className="eyebrow">Audit Log</p>
            <h2 id="processor-trail-title">Invoice timeline</h2>
          </div>
          <button className="ghost-button" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="config-note">Loading audit log…</p>
        ) : trail?.length ? (
          <div className="processor-timeline">
            {trail.map((entry) => (
              <article key={`${entry.step}-${entry.created_at}`} className="processor-timeline__item">
                <div className="processor-timeline__step">Step {entry.step}</div>
                <div className="processor-timeline__content">
                  <strong>{entry.action}</strong>
                  <p>{entry.message || "No message recorded."}</p>
                  <span>
                    {(entry.username || "System")}
                    {entry.userrole ? ` (${entry.userrole})` : ""}
                    {entry.created_at ? ` • ${new Date(entry.created_at).toLocaleString()}` : ""}
                  </span>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="empty-state">No timeline available for this invoice.</p>
        )}
      </div>
    </div>
  );
}

export default function ProcessorPendingActionsView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [statusDetail, setStatusDetail] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);

  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const summary = useMemo(() => {
    const redFlags = rows.reduce(
      (count, row) =>
        count + Object.values(row.invoiceCheck || {}).filter((item) => item?.color === "r").length,
      0,
    );

    return {
      count: rows.length,
      selected: selectedRows.length,
      redFlags,
    };
  }, [rows, selectedRows]);

  
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuPosition) return;

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuPosition(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuPosition]);

  async function loadRows() {
    setLoading(true);
    setError("");

    try {
      const csrfToken = await ensureCsrfToken();
      const response = await fetch("/api/get-hold/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
      });
      const isJson = response.headers.get("content-type")?.includes("application/json");
      const payload = isJson ? await response.json() : null;

      if (!response.ok || payload?.error) {
        throw new Error(
          payload?.message ||
            payload?.error ||
            `Unable to load on-hold invoices. Backend responded with ${response.status}.`,
        );
      }

      if (!payload) {
        throw new Error("Unable to load on-hold invoices. Backend returned a non-JSON response.");
      }

      setRows(payload.data || []);
    } catch (requestError) {
      setError(requestError.message || "Unable to load on-hold invoices.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
  }, []);

  function toggleRowSelection(invoiceGroupKey) {
    setSelectedRows((current) =>
      current.includes(invoiceGroupKey)
        ? current.filter((item) => item !== invoiceGroupKey)
        : [...current, invoiceGroupKey],
    );
  }

  function toggleAllRows() {
    setSelectedRows((current) => (current.length === rows.length ? [] : rows.map((row) => row.invoiceGroupKey)));
  }

  async function handleTrailOpen(row) {
    setTrail(null);
    setTrailLoading(true);

    try {
      const response = await fetch(`/api/invoice/trail/${row.invoiceId}/`, {
        credentials: "include",
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Unable to load trail.");
      }
      setTrail(payload.data || []);
    } catch (requestError) {
      setTrail([]);
      setError(requestError.message || "Unable to load trail.");
    } finally {
      setTrailLoading(false);
    }
  }

  async function handlePreview(row) {
    setStatus("");
    setError("");
    setActionKey(`preview-${row.invoiceGroupKey}`);

    // Open immediately from user click
    const previewWindow = window.open("", "_blank");

    if (!previewWindow) {
      setError("Preview window was blocked by the browser.");
      return;
    }

    previewWindow.document.write(
      "<html><body><h3>Loading preview...</h3></body></html>"
    );

    try {
      const csrfToken = await ensureCsrfToken();

      const response = await fetch("/api/template-preview/", {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify({
          invoice_keys: [row.invoiceGroupKey],
          material_service: row.material_service,
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload?.status !== "success") {
        throw new Error(payload?.message || "Unable to generate preview.");
      }

      let html =
        "<html><head><title>Preview</title></head><body style='padding:24px;font-family:Segoe UI,sans-serif;'>";

      Object.entries(payload.template_html || {}).forEach(([key, value]) => {
        html += `<h3>${key}</h3>${value}<hr/>`;
      });

      html += "</body></html>";

      previewWindow.document.open();
      previewWindow.document.write(html);
      previewWindow.document.close();

    } catch (requestError) {
      previewWindow.close();
      setError(requestError.message || "Unable to generate preview.");
    } finally {
      setActionKey("");
    }
  }

  async function handleAction(row, type) {
    setStatus("");
    setError("");

    if (
      type === "approve" &&
      Object.values(row.invoiceCheck || {}).some((item) => item?.color === "r")
    ) {
      setError("There are discrepancies in this invoice. Fix them before approving.");
      return;
    }

    setActionKey(`${type}-${row.invoiceGroupKey}`);

    try {
      const csrfToken = await ensureCsrfToken();
      let url = "";
      let body = {};

      if (type === "approve") {
        url = "/api/approve-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Processor' };
      } else if (type === "hold") {
        url = "/api/hold-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Processor' };
      } else if (type === "reject") {
        url = "/api/reject-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab:'Processor' };
      } else if (type === "delete") {
        url = "/api/delete-invoice/";
        body = { invoice_group_key: row.invoiceGroupKey, tab: "pendingtab" };
      }

      const response = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-CSRFToken": csrfToken,
        },
        body: JSON.stringify(body),
      });

      const payload = await response.json();
      if (!response.ok || payload.status !== "success") {
        throw new Error(payload.message || `Unable to ${type} invoice.`);
      }

      setStatus(payload.message || `Invoice ${type}d successfully.`);
      setSelectedRows((current) => current.filter((item) => item !== row.invoiceGroupKey));
      await loadRows();
    } catch (requestError) {
      setError(requestError.message || `Unable to ${type} invoice.`);
    } finally {
      setActionKey("");
    }
  }

  // function openLegacyProcessorPage() {
  //   window.localStorage.setItem("activeSection", "uploaded");
  //   window.open(getLegacyUrl("/all-invoices/"), "_blank", "noreferrer");
  // }

  function refreshPendingActions() {
    loadRows();
  }

  return (
    <ProcessorViewShell
      title="Hold Invoices"
      intro=""
      metrics={[
        { label: "Queue", value: summary.count },
        { label: "Selected", value: summary.selected },
        
      ]}
      actions={
        <button
          className="ghost-button processor-view__refresh"
          type="button"
          onClick={refreshPendingActions}
        >
          Refresh
        </button>
      }
    >
      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <div className="workspace-panel processor-table-panel">

        {loading ? (
          <p className="config-note">Loading pending actions…</p>
        ) : (
          <div className="config-table processor-table">
            <table>
              <thead>
                <tr>
                  <th>Invoice ID</th>
                  <th>Vendor Name</th>
                  <th>Invoice No.</th>
                  <th>Invoice Date</th>
                  <th>Invoice Value</th>
                  <th>Status</th>
                  <th>Response</th>
                  <th>File</th>
                  <th>Preview</th>
                  <th>Age</th>
                  <th>
                    Select
                    <input
                      className="processor-checkbox"
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleAllRows}
                    />
                  </th>
                  <th>Logs</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.length ? (
                  rows.map((row) => (
                    <tr key={row.InvoiceGroupKey}>
                      <td>{row.InvoiceId}</td>
                      <td>{row.VendorName || "-"}</td>
                      <td>{row.InvoiceNo || row.InvNo || "-"}</td>
                      <td>{row.InvoiceDate || row.InvDate || "-"}</td>
                      <td>{formatAmount(row.InvoiceValue || row.TotalAmount)}</td>
                      <td>
                        <div className="processor-status-list">
                          {Object.entries(row.InvoiceCheck || {}).length ? (
                            Object.entries(row.InvoiceCheck || {}).map(([label, item]) => (
                              <button
                                key={`${row.InvoiceGroupKey}-${label}`}
                                className={`processor-status-dot processor-status-dot--${item?.color || "o"}`}
                                type="button"
                                title={`${label}: ${item?.message || ""}`}
                                onClick={() =>
                                  setStatusDetail({
                                    label,
                                    message: item?.message,
                                    data: item?.data,
                                    key: item?.key || label,
                                    checkKey: label,
                                    uniqueKey: row.InvoiceGroupKey,
                                    color: item?.color || "o",
                                  })
                                }
                              />
                            ))
                          ) : (
                            <span className="empty-state">No checks</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {row.unique_name ? (
                          <button
                            onClick={() =>
                              openInvoiceData(
                                `${row.unique_name}.json`
                              )
                            }
                          >
                            Data
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        {row.unique_name ? (
                          <button
                            type="button"
                            onClick={() => openInvoicePdf(row.InvoiceId)}
                          >
                            📎
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td>
                        <button
                          className="ghost-button"
                          type="button"
                          disabled={actionKey === `preview-${row.InvoiceGroupKey}`}
                          onClick={() => handlePreview(row)}
                        >
                          {actionKey === `preview-${row.InvoiceGroupKey}` ? "Previewing..." : "Preview"}
                        </button>
                      </td>
                      <td>{getPendingDuration(row.CreatedAt)}</td>
                      <td>
                        <input
                          className="processor-checkbox"
                          type="checkbox"
                          checked={selectedRows.includes(row.InvoiceGroupKey)}
                          onChange={() => toggleRowSelection(row.InvoiceGroupKey)}
                        />
                      </td>
                      <td>
                        <button className="ghost-button" type="button" onClick={() => handleTrailOpen(row)}>
                          Logs
                        </button>
                      </td>
                      <td>
                        <div className="action-dropdown-wrapper">
                          <button
                            type="button"
                            className="action-menu-button"
                            onClick={(e) => {

                              if (
                                menuPosition?.row?.InvoiceGroupKey === row.InvoiceGroupKey
                              ) {
                                setMenuPosition(null);
                                return;
                              }

                              const rect = e.currentTarget.getBoundingClientRect();

                              setMenuPosition({
                                row,
                                top: rect.bottom + window.scrollY,
                                left: rect.left + window.scrollX,
                              });
                            }}
                          >
                            Actions ▼
                          </button>                         
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="12">No pending processor actions found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {menuPosition && (
        <div
          ref={menuRef}
          className="global-action-menu"
          style={{
            position: "fixed",
            top: menuPosition.top,
            left: menuPosition.left,
            zIndex: 999999,
          }}
        >
          <button
            type="button"
            onClick={() => {
              handleAction(menuPosition.row, "approve");
              setMenuPosition(null);
            }}
          >
            ✓ Approve
          </button>

          <button
            type="button"
            onClick={() => {
              handleAction(menuPosition.row, "reject");
              setMenuPosition(null);
            }}
          >
            ✕ Reject
          </button>

          <button
            type="button"
            onClick={() => {
              setEditTarget({
                invoiceGroupKey: menuPosition.row.invoiceGroupKey,
              });
              setMenuPosition(null);
            }}
          >
            ✎ Edit
          </button>

          <button
            type="button"
            className="danger-item"
            onClick={() => {
              handleAction(menuPosition.row, "delete");
              setMenuPosition(null);
            }}
          >
            🗑 Delete
          </button>
        </div>
      )}

      <InvoiceCheckDetailModal
        detail={statusDetail}
        onClose={() => setStatusDetail(null)}
        onSaved={loadRows}
      />
      <ProcessorInvoiceEditModal
        invoiceGroupKey={editTarget?.invoiceGroupKey}
        onClose={() => setEditTarget(null)}
        onSaved={loadRows}
      />
      <TrailModal trail={trail} loading={trailLoading} onClose={() => setTrail(null)} />
    </ProcessorViewShell>
  );
}
